import * as React from "react";

const SPOTLIGHT_MS = 2000;

export type GoalSpotlightApi = {
  /** Goal ids highlighted after completing a linked habit (empty when idle). */
  spotlightGoalIds: readonly string[];
  /** Call when a goal-linked instance is marked complete; extends spotlight and resets the timer. */
  recordGoalTaskCompletion(goalId: string): void;
};

const GoalSpotlightContext = React.createContext<GoalSpotlightApi | null>(null);

const idleApi: GoalSpotlightApi = {
  spotlightGoalIds: [],
  recordGoalTaskCompletion: () => {},
};

export function GoalSpotlightProvider({ children }: { children: React.ReactNode }) {
  const [spotlightGoalIds, setSpotlightGoalIds] = React.useState<string[]>([]);
  const clearTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const recordGoalTaskCompletion = React.useCallback((goalId: string) => {
    setSpotlightGoalIds((prev) => [...new Set([...prev, goalId])]);
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    clearTimerRef.current = setTimeout(() => {
      clearTimerRef.current = null;
      setSpotlightGoalIds([]);
    }, SPOTLIGHT_MS);
  }, []);

  React.useEffect(
    () => () => {
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    },
    [],
  );

  const value = React.useMemo(
    (): GoalSpotlightApi => ({
      spotlightGoalIds,
      recordGoalTaskCompletion,
    }),
    [spotlightGoalIds, recordGoalTaskCompletion],
  );

  return <GoalSpotlightContext.Provider value={value}>{children}</GoalSpotlightContext.Provider>;
}

/** Planner / accordion / task list — safe outside provider (no-op completion, empty spotlight). */
export function useGoalSpotlight(): GoalSpotlightApi {
  return React.useContext(GoalSpotlightContext) ?? idleApi;
}
